import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import DecorBackground from '@/components/marketing/DecorBackground';
import CyberOperator from '@/components/marketing/CyberOperator';
import WorldMap from '@/components/marketing/WorldMap';
import StatsStrip from '@/components/marketing/StatsStrip';
import { STATS } from '@/data/site-content';

const CHIPS = ['Zero-Click', 'Domain Redirect', 'Pop', 'Push', 'Source Controls', 'Fraud Filtering'];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#050B17] text-white isolate" data-testid="home-hero-section">
      <DecorBackground variant="hero" nodes />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 lg:pt-24 pb-16 sm:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <motion.div className="lg:col-span-7" initial="hidden" animate="show" variants={stagger}>
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[12px] font-medium text-[#22D3EE] backdrop-blur-md"
            >
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#22D3EE] opacity-75 pulse-glow" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22D3EE]" />
              </span>
              Global Performance Traffic Platform
            </motion.span>

            <motion.h1
              variants={fadeUp}
              className="font-display-tight mt-6 text-[44px] leading-[1.02] sm:text-[60px] lg:text-[78px] font-bold tracking-[-0.04em] text-white"
            >
              Scale Campaigns Through{' '}
              <span className="relative inline-block">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#22D3EE] via-[#60A5FA] to-[#A78BFA]">
                  Premium Traffic
                </span>
              </span>{' '}
              Infrastructure
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-6 text-[17px] sm:text-[19px] leading-[1.65] text-slate-300/90 max-w-2xl"
            >
              Ads Dashbound connects advertisers with curated traffic flows, source-level controls, fraud filtering, and managed campaign support — built for teams that need quality, speed, and scale.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                data-testid="hero-primary-cta-button"
                className="btn-primary-gradient text-white rounded-xl px-7 py-6 text-[15px] font-semibold border-0"
              >
                <Link to="/apply" className="inline-flex items-center gap-2">
                  Apply as Advertiser <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="hero-secondary-cta-button"
                className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-6 py-6 text-[15px] font-semibold transition-colors backdrop-blur-md"
              >
                <Link to="/ad-formats" className="inline-flex items-center gap-2">
                  Explore Traffic Formats
                </Link>
              </Button>
            </motion.div>

            <motion.ul variants={fadeUp} className="mt-8 flex flex-wrap gap-2">
              {CHIPS.map((c) => (
                <li
                  key={c}
                  className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-[12px] font-medium text-slate-200 backdrop-blur-md hover:border-[#22D3EE]/40 hover:text-white transition-colors"
                >
                  {c}
                </li>
              ))}
            </motion.ul>

            <motion.div variants={fadeUp} className="mt-7 flex items-center gap-3 text-[12px] text-slate-400">
              <Sparkles className="h-3.5 w-3.5 text-[#22D3EE]" />
              <span>Built for serious advertisers — no public pricing plans, top-up funded campaigns.</span>
            </motion.div>
          </motion.div>

          {/* Right: cyberpunk operator scene */}
          <div className="lg:col-span-5 relative">
            {/* Soft holographic dotted map behind operator */}
            <div className="absolute inset-0 -z-10 opacity-[0.35] pointer-events-none" aria-hidden>
              <WorldMap intensity="high" showLabels={false} />
            </div>
            <CyberOperator />
          </div>
        </div>

        <StatsStrip items={STATS} />
      </div>
    </section>
  );
}
